import { LoadingButton } from '@atlaskit/button';
import SectionMessage from '@atlaskit/section-message';
import Spinner from '@atlaskit/spinner';
import React, { useEffect } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { Action, redirect } from 'redux-first-router';

import { State } from 'model/State';
import t from 'modules/i18n/intl';
import { licensesPreselected } from 'modules/mac/duck/actions';
import { Form, ErrorMessage } from 'modules/stripe-credit-card';
import { useStripeOnSubmitMAC } from 'util/mac/stripeUtil';

import MACPageHeader from './components/macPageHeader';
import {
  BillingStatementNote,
  CCFormWrapper,
  HeaderContainer,
  LinkButton,
  PageH3Header,
  PageTitle,
} from './components/styledComponents';
import { StyledValueWrapper, StyledWrapper } from './components/PaymentMethodsTable';
import { messages } from './messages';
import {
  selectDetails,
  selectDetailsGeneralObject,
  selectEnableAutoRenew,
  selectEnableAutoRenewProgress,
} from './duck/selectors';
import { postEnableAutoRenew } from './duck/actions/creditCard';
import { route } from './duck/actions/landing';
import { checkFeatureGate, FeatureFlags } from 'util/statsig';
import { CloudProductKeys } from 'data/products/keys';

const LicenseInfoAndStripeCCForm = ({ accountDetails, accountId }) => {
  const dispatch = useDispatch();

  const enabledAutoRenew = useSelector(selectEnableAutoRenewProgress);

  return (
    <>
      <StyledWrapper>
        <StyledValueWrapper isBold width="50%">
          <FormattedMessage {...messages.productTableTitle} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="25%">
          <FormattedMessage {...messages.nameTableTitle} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="15%">
          <FormattedMessage {...messages.SEN} />
        </StyledValueWrapper>
        <StyledValueWrapper isBold width="10%">
          <FormattedMessage {...messages.expiryDate} />
        </StyledValueWrapper>
      </StyledWrapper>
      <StyledWrapper data-cy="mac-enable-auto-renew-product-line">
        <StyledValueWrapper width="50%">
          <p>{accountDetails.productDescription}</p>
        </StyledValueWrapper>
        <StyledValueWrapper width="25%">
          <p>{accountDetails.name}</p>
        </StyledValueWrapper>
        <StyledValueWrapper width="15%">
          <p>{`SEN-${accountId}`}</p>
        </StyledValueWrapper>
        <StyledValueWrapper width="10%">
          <p>
            <FormattedDate
              value={new Date(accountDetails.latestPeriod?.endDate)}
              day="2-digit"
              month="short"
              year="numeric"
            />
          </p>
        </StyledValueWrapper>
      </StyledWrapper>
      <PageH3Header>
        <FormattedMessage
          defaultMessage="Credit Card Details"
          id="mac.auto-renew.page.cc-details.title"
        />
      </PageH3Header>
      <form
        onSubmit={useStripeOnSubmitMAC(() => {
          dispatch(postEnableAutoRenew(accountId));
        })}
      >
        <CCFormWrapper>
          <Form autoFocus frameEmbeddingMitigation={false} />
          <ErrorMessage />
        </CCFormWrapper>
        <br />
        <LoadingButton
          data-cy="update-cc-submit-button"
          appearance="primary"
          type="submit"
          isLoading={enabledAutoRenew.inProgress}
        >
          <FormattedMessage
            defaultMessage="Save Auto-Renew Settings"
            id="mac.auto-renew.page.submit-button"
          />
        </LoadingButton>{' '}
        <LinkButton data-cy="update-cc-cancel-button" href={`/purchase/my/licenses/${accountId}`}>
          <FormattedMessage defaultMessage="Cancel" id="mac.auto-renew.page.cancel-button" />
        </LinkButton>
      </form>
      <BillingStatementNote>
        <FormattedMessage
          defaultMessage="Your billing statement will display Atlassian B.V., located in Amsterdam, Netherlands. Atlassian Pty Limited, our principal place of business, is at Level 6, 341 George Street, Sydney NSW Australia 2000. Your credit card issuer may charge foreign transaction or cross-border fees in addition to the total price above."
          id="mac.update-cc-page.billing-statement-note"
        />
      </BillingStatementNote>
    </>
  );
};

const b = (chunks) => <strong>{chunks}</strong>;

const EnableAutoRenewPage = () => {
  const MACCloudReadModeFG = checkFeatureGate(FeatureFlags.CART_MAC_CLOUD_READ_MODE);

  const dispatch = useDispatch();

  const accountId = useSelector((state: State) => state.location.payload.accountId);
  const accountDetails: any = useSelector((state: State) => selectDetails(state, accountId));
  const accountDetailsProgress: any = useSelector(selectDetailsGeneralObject)?.[accountId];
  const enabledAutoRenewResult = useSelector(selectEnableAutoRenew);

  useEffect(() => {
    document.title = 'My Atlassian | Auto-Renew';
  }, [accountId, accountDetails, enabledAutoRenewResult]);

  useEffect(() => {
    if (accountDetails) {
      const productPackage =
        accountDetails.merchandise?.productPackage || accountDetails.productPackage;
      if (
        productPackage?.hostingType === 'HOSTED' &&
        productPackage.key !== CloudProductKeys.BITBUCKET &&
        MACCloudReadModeFG
      ) {
        dispatch(redirect(route() as Action));
      }
    }
  }, [accountDetails, MACCloudReadModeFG]);

  useEffect(() => {
    if (enabledAutoRenewResult === '') {
      dispatch(
        redirect(licensesPreselected.licensePreselectedRoute(accountId.toString(), {}) as Action),
      );
    }
  }, [enabledAutoRenewResult]);

  const ContentComponent =
    typeof accountDetailsProgress?.result === 'string' ? (
      <SectionMessage appearance="error">{accountDetailsProgress.result}</SectionMessage>
    ) : (
      <LicenseInfoAndStripeCCForm accountDetails={accountDetails} accountId={accountId} />
    );

  return (
    <>
      <MACPageHeader />
      <HeaderContainer>
        <PageTitle>
          <FormattedMessage defaultMessage="Auto-Renew Product" id="mac.auto-renew.page.header" />
        </PageTitle>
      </HeaderContainer>
      <p>
        {t(
          {
            defaultMessage:
              'Atlassian will automatically renew the software maintenance for the product <b>using the credit card details provided</b>. Our prices may change in the future but we will notify you before the renewal, and you can always update your renewal settings at any time in your account.',
            id: 'mac.auto-renew.page.content',
          },
          { b },
        )}
      </p>
      {!accountDetailsProgress || accountDetailsProgress?.inProgress ? (
        <Spinner size="large" />
      ) : (
        ContentComponent
      )}
    </>
  );
};

export default EnableAutoRenewPage;
